import {
  LayoutDashboard,
  ClipboardList,
  Compass,
  FolderHeart,
  GraduationCap,
  BookOpen,
  ClipboardCheck,
  PenLine,
  MessageSquareText,
  LifeBuoy,
  Sparkles,
  PlayCircle,
  ArrowRight,
  CheckCircle2,
  Circle,
  Clock,
  Lock,
  type LucideIcon,
} from "lucide-react";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", active: true },
  { icon: ClipboardList, label: "Opdrachten" },
  { icon: Compass, label: "Keuzehulp" },
  { icon: FolderHeart, label: "Mijn dossier" },
  { icon: GraduationCap, label: "Opleidingen" },
];

type ItemStatus = "done" | "todo" | "locked" | "next";

const items: Array<{
  type: "reading" | "task" | "reflection" | "test";
  title: string;
  meta: string;
  status: ItemStatus;
}> = [
  {
    type: "reading",
    title: "Wat is LOB eigenlijk?",
    meta: "Lezen · 5 min",
    status: "done",
  },
  {
    type: "task",
    title: "Mijn tijdlijn: wie ben ik nu?",
    meta: "Opdracht · ingeleverd",
    status: "done",
  },
  {
    type: "test",
    title: "Interessetest",
    meta: "Test · ± 15 min",
    status: "next",
  },
  {
    type: "reflection",
    title: "Terugblik op je stagedag",
    meta: "Nadenken · 3 vragen",
    status: "todo",
  },
  {
    type: "test",
    title: "Talententest",
    meta: "Test · beschikbaar na interessetest",
    status: "locked",
  },
];

const TYPE_META: Record<
  string,
  { label: string; Icon: LucideIcon; badge: string; icon: string }
> = {
  reading: {
    label: "Lezen",
    Icon: BookOpen,
    badge: "bg-sky-100 text-sky-700",
    icon: "bg-sky-500",
  },
  task: {
    label: "Opdracht",
    Icon: ClipboardCheck,
    badge: "bg-violet-100 text-violet-700",
    icon: "bg-violet-500",
  },
  reflection: {
    label: "Nadenken",
    Icon: PenLine,
    badge: "bg-amber-100 text-amber-700",
    icon: "bg-amber-500",
  },
  test: {
    label: "Test",
    Icon: MessageSquareText,
    badge: "bg-primary/15 text-primary",
    icon: "bg-primary",
  },
};

const PortalMockup = () => {
  return (
    <div className="rounded-[1.5rem] border border-border bg-card shadow-large overflow-hidden select-none">
      {/* browser bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/50">
        <span className="w-3 h-3 rounded-full bg-destructive/40" />
        <span className="w-3 h-3 rounded-full bg-warm/50" />
        <span className="w-3 h-3 rounded-full bg-primary/40" />
        <div className="ml-3 flex-1 max-w-xs">
          <div className="h-6 rounded-full bg-background border border-border flex items-center px-3 text-[11px] text-muted-foreground">
            portal.studie-connect.nl
          </div>
        </div>
      </div>

      {/* app body */}
      <div className="flex bg-muted/30">
        {/* sidebar */}
        <aside className="hidden sm:flex w-40 shrink-0 flex-col border-r border-border bg-card p-3">
          <div className="flex items-center gap-2 px-1.5 pb-4 mb-1">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center text-primary-foreground text-xs font-extrabold">
              sc
            </div>
            <span className="text-xs font-extrabold text-secondary">studie connect</span>
          </div>
          <nav className="space-y-1">
            {navItems.map((n) => (
              <div
                key={n.label}
                className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[12px] font-semibold ${
                  n.active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground"
                }`}
              >
                <n.icon className="w-4 h-4 shrink-0" />
                {n.label}
              </div>
            ))}
          </nav>
          <div className="mt-auto flex items-center gap-2 pt-4 border-t border-border">
            <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-[10px] font-extrabold text-primary">
              SV
            </div>
            <div className="leading-tight">
              <div className="text-[11px] font-bold text-secondary">Sanne</div>
              <div className="text-[9px] text-muted-foreground">2 havo</div>
            </div>
          </div>
        </aside>

        {/* main */}
        <div className="flex-1 p-4 sm:p-5 space-y-4 min-w-0">
          {/* greeting */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-muted-foreground bg-background border border-border rounded-full px-2 py-1 mb-2">
                <Sparkles className="w-2.5 h-2.5 text-primary" />
                Leerjaar 2 · Jouw LOB-jaar
              </div>
              <h3 className="text-xl sm:text-2xl font-extrabold text-secondary leading-tight">
                Hoi Sanne <span className="inline-block">👋</span>
              </h3>
              <p className="text-[11px] sm:text-xs text-muted-foreground mt-1">
                Dit jaar ontdek je welke talenten je hebt en welke richtingen daarbij passen.
              </p>
            </div>
            <div className="hidden sm:inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-[hsl(196,100%,54%)] text-primary-foreground px-3 py-2 text-[11px] font-bold shadow-soft shrink-0">
              <LifeBuoy className="w-3.5 h-3.5" />
              Hulp vragen
            </div>
          </div>

          {/* progress band */}
          <div className="flex items-center gap-3 pt-1">
            <div>
              <div className="text-xs font-extrabold text-secondary">Jouw opdrachten</div>
              <div className="text-[10px] text-muted-foreground">Nog 3 te doen · 2 klaar</div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <div className="w-24 sm:w-36 h-2 rounded-full bg-slate-200 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: "40%",
                    background: "linear-gradient(90deg, #3b82f6, #06b6d4)",
                  }}
                />
              </div>
              <span className="text-xs font-extrabold text-slate-700 tabular-nums">40%</span>
            </div>
          </div>

          {/* items list */}
          <div className="space-y-2">
            {items.map((it, i) => {
              const meta = TYPE_META[it.type];
              const isNext = it.status === "next";
              const isDone = it.status === "done";
              const isLocked = it.status === "locked";
              return (
                <div
                  key={i}
                  className={`flex items-center gap-3 rounded-xl border bg-card px-3 py-2.5 shadow-soft ${
                    isNext
                      ? "border-primary/40 ring-2 ring-primary/10"
                      : "border-border"
                  } ${isLocked ? "opacity-60" : ""}`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.icon}`}
                  >
                    <meta.Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider ${meta.badge}`}
                      >
                        {meta.label}
                      </span>
                      {isNext && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider">
                          <Sparkles className="w-2 h-2" /> Nu doen
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] sm:text-[12px] font-bold text-secondary leading-tight mt-0.5 truncate">
                      {it.title}
                    </div>
                    <div className="text-[9px] text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {it.meta}
                    </div>
                  </div>
                  <div className="shrink-0">
                    {isDone && (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    )}
                    {isNext && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-2.5 py-1.5 text-[10px] font-bold shadow-sm">
                        <PlayCircle className="w-3 h-3" />
                        Start
                        <ArrowRight className="w-2.5 h-2.5" />
                      </span>
                    )}
                    {it.status === "todo" && (
                      <Circle className="w-5 h-5 text-muted-foreground/50" />
                    )}
                    {isLocked && (
                      <Lock className="w-4 h-4 text-muted-foreground/60" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalMockup;
